import { useEffect, useState } from "react";

import { Button, CircularProgress, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  AddCarrierToNetworkButton,
  FollowShipperRequestSource,
} from "components/Shipper/components/AddCarrierToNetworkButton";
import { ClientAppEvent, ShipmentMicroReviewSource } from "constants/index";
import { useShipmentMicroReview } from "hooks";
import { useCompanyReviewEligibility } from "hooks/useCompanyReviewEligibility";
import { useShipperNetwork } from "hooks/useShipperNetwork";
import { getBookedQuoteForShipperCondensedShipment } from "utils/quotes";

import { InlineCompanyReviewPrompt } from "./InlineCompanyReviewPrompt";

interface Props {
  shipment: CondensedShipment;
  onShipmentMicroReviewSuccess: VoidFunction | null;
}

export function ShipmentMicroReviewMenu(props: Props) {
  const { shipment, onShipmentMicroReviewSuccess } = props;
  const posthog = usePostHog();
  const bookedQuote = getBookedQuoteForShipperCondensedShipment(
    shipment as ShipperCondensedShipment,
  );
  const carrierCompanyName = bookedQuote?.created_by_company?.name ?? "-";

  const carrierCompanyId = bookedQuote?.created_by_company?.id ?? null;

  const [showAddPartnerButton, setShowAddPartnerButton] = useState(false);
  const { followingCarriers } = useShipperNetwork();

  const {
    canReview,
    reviewExists,
    isPositive,
    loading,
    error,
    submitReview,
    submitting,
    submitError,
  } = useShipmentMicroReview({
    shipmentId: shipment.info.id,
    onSuccess: onShipmentMicroReviewSuccess ?? undefined,
  });

  const { canReview: canReviewCompany, loading: eligibilityLoading } =
    useCompanyReviewEligibility({
      companyId: reviewExists ? carrierCompanyId : null,
    });

  // Check if we should show the add partner button for existing positive reviews
  useEffect(() => {
    if (reviewExists && isPositive === true && bookedQuote?.carrier_id) {
      const carrierId = bookedQuote.carrier_id;
      const isCarrierInNetwork = followingCarriers.some(
        (carrier) => carrier.id === carrierId,
      );

      if (!isCarrierInNetwork) {
        setShowAddPartnerButton(true);
      } else {
        setShowAddPartnerButton(false);
      }
    } else {
      setShowAddPartnerButton(false);
    }
  }, [reviewExists, isPositive, bookedQuote?.carrier_id, followingCarriers]);

  // Show loading spinner while fetching status
  if (loading) {
    return <CircularProgress />;
  }

  // Hide component if user can't review and no review exists
  if (!canReview && !reviewExists) {
    return null;
  }

  const handleYesClick = () => {
    submitReview(true, () => {
      posthog?.capture(ClientAppEvent.SHIPMENT_MICRO_REVIEW_SUBMITTED, {
        source: ShipmentMicroReviewSource.IN_APP_SHIPMENT_DETAIL,
        shipment_id: shipment.info.id,
        is_positive: true,
      });
    });
  };

  const handleNoClick = () => {
    submitReview(false, () => {
      posthog?.capture(ClientAppEvent.SHIPMENT_MICRO_REVIEW_SUBMITTED, {
        source: ShipmentMicroReviewSource.IN_APP_SHIPMENT_DETAIL,
        shipment_id: shipment.info.id,
        is_positive: false,
      });
    });
  };

  if (reviewExists) {
    const carrierId = bookedQuote?.carrier_id;

    return (
      <Stack mt="1rem" mb="1rem" spacing={1}>
        <Typography color="green">Review Submitted. Thank you!</Typography>
        {showAddPartnerButton && carrierId && (
          <Stack direction="row">
            <AddCarrierToNetworkButton
              carrierId={carrierId}
              source={FollowShipperRequestSource.SHIPMENT_MICRO_REVIEW}
              buttonVariant="contained"
              checkForRequestAlreadySent
            />
          </Stack>
        )}
        {!eligibilityLoading && canReviewCompany && carrierCompanyId && (
          <InlineCompanyReviewPrompt
            carrierCompanyId={carrierCompanyId}
            carrierCompanyName={carrierCompanyName}
            shipmentId={shipment.info.id}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack mt="1rem" mb="1rem" spacing={1}>
      <Typography>
        Were you happy with this service provided by {carrierCompanyName} on
        this shipment?
      </Typography>
      <LoadingWrappedNode loading={submitting}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="success"
            onClick={handleYesClick}
            disabled={submitting}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleNoClick}
            disabled={submitting}
          >
            No
          </Button>
        </Stack>
      </LoadingWrappedNode>
      <Typography variant="body2" color="text.secondary">
        Satisfaction ratings are anonymous. Carriers only see their aggregate
        score.
      </Typography>
      <ErrorTypography error={error ?? submitError} />
    </Stack>
  );
}
